export const handleRelayout = (updateMapView) => (eventData) => {
  if (eventData['mapbox.center'] && eventData['mapbox.zoom']) {
    const newCenter = {
      lat: eventData['mapbox.center'].lat,
      lon: eventData['mapbox.center'].lon,
    };
    const newZoom = eventData['mapbox.zoom'];

    if (eventData['mapbox._derived']) {
      const coordinates = eventData['mapbox._derived'].coordinates;
      const newBounds = {
        max_lat: coordinates[0][1],
        min_lat: coordinates[2][1],
        max_lon: coordinates[1][0],
        min_lon: coordinates[0][0],
      };

      updateMapView({ ...newBounds, zoom: newZoom, center: newCenter });
    } else {
      updateMapView({ center: newCenter, zoom: newZoom });
    }
  }
};

export const toSliderScale = (value, min, max) => {
  const logMin = Math.log(min);
  const logMax = Math.log(max);
  const logValue = Math.log(value);
  const sliderValue = ((logValue - logMin) / (logMax - logMin)) * 1000;
  return sliderValue;
};

export const fromSliderScale = (sliderValue, min, max) => {
  const logMin = Math.log(min);
  const logMax = Math.log(max);
  const logValue = ((sliderValue / 1000) * (logMax - logMin)) + logMin;
  const value = Math.exp(logValue);
  return value;
};

export const toDateSliderScale = (date, minDateStr, maxDateStr) => {
  const minDate = new Date(minDateStr).getTime();
  const maxDate = new Date(maxDateStr).getTime();
  const dateTimestamp = new Date(date).getTime();
  const sliderValue = ((dateTimestamp - minDate) / (maxDate - minDate)) * 1000;
  return sliderValue;
};

export const fromDateSliderScale = (sliderValue, minDateStr, maxDateStr) => {
  const minDate = new Date(minDateStr).getTime();
  const maxDate = new Date(maxDateStr).getTime();
  const dateTimestamp = ((sliderValue / 1000) * (maxDate - minDate)) + minDate;
  return new Date(dateTimestamp).toISOString().split('T')[0];
};

export const isPointInPolygon = (point, polygon) => {
  const [x, y] = point;
  let inside = false;
  
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    
    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  
  return inside;
};

export const aggregateDataByMonth = (data) => {
  // Group by month start (first day of month)
  const monthlyData = {};
  
  data.forEach(item => {
    const itemDate = new Date(item.date);
    // Create month start key (YYYY-MM-01) - use string formatting to avoid timezone issues
    const year = itemDate.getFullYear();
    const month = itemDate.getMonth() + 1; // Convert from 0-indexed to 1-indexed
    const monthKey = `${year}-${month.toString().padStart(2, '0')}-01`;
    
    if (!monthlyData[monthKey]) {
      monthlyData[monthKey] = {
        prices: [],
        date: monthKey
      };
    }
    monthlyData[monthKey].prices.push(item.price);
  });

  // Convert to array and sort by date
  const monthlyArray = Object.keys(monthlyData)
    .sort()
    .map(monthKey => {
      const monthInfo = monthlyData[monthKey];
      const prices = monthInfo.prices;
      const count = prices.length;
      
      // Calculate statistics
      const mean = prices.reduce((sum, price) => sum + price, 0) / count;
      const sortedPrices = [...prices].sort((a, b) => a - b);
      const median = count % 2 === 0 
        ? (sortedPrices[Math.floor(count / 2) - 1] + sortedPrices[Math.floor(count / 2)]) / 2
        : sortedPrices[Math.floor(count / 2)];
      const geometricMean = Math.exp(prices.reduce((sum, price) => sum + Math.log(price), 0) / count);
      
      return {
        date: monthKey,
        count: count,
        mean: mean,
        median: median,
        geometric_mean: geometricMean,
        geometric_mean_12_month_window: geometricMean // Will be calculated below
      };
    });

  // Calculate 12-month rolling windows (center=True, min_periods=2)
  monthlyArray.forEach((month, index) => {
    const windowStart = Math.max(0, index - 5); // 6 months before
    const windowEnd = Math.min(monthlyArray.length, index + 7); // 6 months after + current
    const windowData = monthlyArray.slice(windowStart, windowEnd);
    
    if (windowData.length >= 2) {
      const validGeometricMeans = windowData
        .map(d => d.geometric_mean)
        .filter(val => !isNaN(val) && isFinite(val));
      
      if (validGeometricMeans.length >= 2) {
        month.geometric_mean_12_month_window = 
          validGeometricMeans.reduce((sum, val) => sum + val, 0) / validGeometricMeans.length;
      }
    }
  });

  return monthlyArray;
};